import React, { useMemo } from 'react';
import { useIntl } from 'umi';
import classNames from 'classnames';
import { LANGUAGE_EN } from '@home/_config/config';
import TableAnalytics from '@/pages/datalab/components/CustomComponents/TableAnalytics';
import SvgIcon from '@home/components/SvgIcon';
import { Bubble, Icon } from 'tea-component';
import { ITableSortItem } from '../../types';
import { IDataTableProps } from './types';
import { convertApiDataToTableFormat } from '../../utils/tableDataConverter';
import { buildAbExperimentConditionalFormat } from '../../utils/abExperimentTableBarPercent';
import { buildAdaptiveColumnWidths } from './utils';
import './index.scss';

const DataTable: React.FC<IDataTableProps> = (props) => {
  const {
    table,
    chartConfig,
    onChartConfigChange,
    handleDownload,
    limit,
    isOverLimit,
  } = props;
  const { locale, formatMessage } = useIntl();
  const isTranspose = Boolean(chartConfig?.isTranspose);
  const isEn = locale === LANGUAGE_EN;
  const currentWidthLocale = isEn ? 'en' : 'cn';

  const tableData = useMemo(() => {
    if (!table) {
      return {
        columnName: [],
        data: [],
      };
    }
    return convertApiDataToTableFormat(table, isTranspose, limit);
  }, [table, isTranspose, limit]);

  const handleSaveTableState = (
    state: { sorts?: ITableSortItem[]; columnWidths?: number[]; }
  ): void => {
    if (!onChartConfigChange) {
      return;
    }

    if (state.sorts !== undefined || state.columnWidths !== undefined) {
      const nextWidthMap = {
        ...(chartConfig?.tableColumnWidthsMap || {}),
        [currentWidthLocale]: state.columnWidths,
      };
      onChartConfigChange({
        ...(state.sorts !== undefined && { sorts: state.sorts }),
        ...(state.columnWidths !== undefined && {
          // 仅存储 map：en/cn 分别维护列宽，不再写入旧字段 tableColumnWidths。
          tableColumnWidthsMap: nextWidthMap,
        }),
      });
    }
  };
  const saveTableState = onChartConfigChange ? handleSaveTableState : undefined;
  const isShowOverLimit = isOverLimit || (limit && tableData?.count && tableData?.count >= limit);
  const showIcon = Boolean(tableData?.data?.length);
  const widthMap = chartConfig?.tableColumnWidthsMap;
  const hasWidthMap = Boolean(widthMap);
  // 优先使用当前语言对应的 map 宽度。
  // 仅当完全没有 map（旧数据）时才回退 tableColumnWidths。
  // 若 map 已存在但当前语言缺失（如仅有 en），则不回退旧字段，直接走自适应重算。
  const persistedTableColumnWidths = hasWidthMap
    ? widthMap?.[currentWidthLocale]
    : chartConfig?.tableColumnWidths;
  const hasPersistedWidths =
    Array.isArray(persistedTableColumnWidths) &&
    persistedTableColumnWidths.length === tableData?.columnName?.length &&
    persistedTableColumnWidths.length > 0;
  const adaptiveTableColumnWidths = useMemo((): number[] => {
    // 已有持久化列宽时不再重复计算，避免无意义的遍历和字符长度估算。
    if (hasPersistedWidths) {
      return [];
    }
    return buildAdaptiveColumnWidths(
      tableData?.columnName ?? [],
      tableData?.data ?? [],
      isEn,
    );
  }, [hasPersistedWidths, isEn, tableData?.columnName, tableData?.data]);

  // 图表配置
  const newChartConfig = useMemo(() => {
    const { sorts = [] } = chartConfig || {};
    let resolvedTableColumnWidths: number[] | undefined;
    // 优先级：用户拖拽持久化宽度 > 自适应计算宽度。
    if (hasPersistedWidths) {
      resolvedTableColumnWidths = persistedTableColumnWidths;
    } else if (adaptiveTableColumnWidths.length) {
      resolvedTableColumnWidths = adaptiveTableColumnWidths;
    }
    // 构建占比条配置
    const abConditionalFormat = buildAbExperimentConditionalFormat(
      tableData?.columnName,
      isTranspose,
    );
    return {
      sorts,
      tableColumnWidths: resolvedTableColumnWidths,
      freeze: {
        show: !isTranspose,
        count: 1,
      },
      ...(abConditionalFormat && { conditionalFormat: abConditionalFormat }),
    };
  }, [
    adaptiveTableColumnWidths,
    chartConfig,
    hasPersistedWidths,
    isTranspose,
    isEn,
    persistedTableColumnWidths,
    tableData?.columnName,
  ]);

  return (
    <div className="dbl-data-table">
      <div className="dbl-data-table-operation">
        <div className={classNames('dbl-data-table-operation-over-limit', { 'dbl-data-table-operation-show': isShowOverLimit })}>
          <Icon type="warning" />
          {formatMessage({ id: 'dbl_chart_row_over_limit' })}
        </div>
        {showIcon && (
          <div className="dbl-data-table-operation-icon-group">
            <Bubble dark content={formatMessage({ id: 'dbl_common_transpose' })}>
              <div
                className={classNames('dbl-data-table-operation-icon', { 'dbl-data-table-operation-icon-active': isTranspose })}
                onClick={() =>
                  onChartConfigChange?.({
                    isTranspose: !isTranspose,
                  })
                }
              >
                <SvgIcon className="dbl-data-table-operation-svg" type="transpose" />
              </div>
            </Bubble>
            <Bubble dark content={formatMessage({ id: 'dbl_common_download' })}>
              <div className="dbl-data-table-operation-icon" onClick={handleDownload}>
                <SvgIcon className="dbl-data-table-operation-svg" type="download" />
              </div>
            </Bubble>
          </div>
        )}
      </div>
      <TableAnalytics
        key={`transpose-${Number(isTranspose)}-${isEn}`}
        className="dbl-data-table-analytics"
        isEn={isEn}
        table={tableData}
        chartConfig={newChartConfig}
        saveTableState={saveTableState}
        sortable={true}
        isColumnResize={true}
      />
    </div>
  );
};

export default DataTable;

